import { css } from 'styled-components';
import {
  popoverCommonStyle,
  popoverDefaultStyle,
  popoverSecondaryStyle,
  popoverPromotionalStyle,
  popoverNextStyle,
} from './popover/styles';
import { tooltipDefaultStyle } from './tooltip/styles';
import { utilities } from './utilities';

/**
 * Next variant CSS value fragments — compose with component-specific selectors.
 * Each fragment contains only property declarations (no selectors) so components
 * can wrap them with their own Ant-specific class selectors and add !important
 * where needed to beat Ant's specificity.
 */
export const nextVariant = {
  hover: css`
    border-color: ${({ theme }) => theme.token.next.colorBorderHover};
    box-shadow: ${({ theme }) => theme.token.next.boxShadowHover};
  `,
  focus: css`
    border-color: ${({ theme }) => theme.token.next.colorBorderFocus};
    box-shadow: ${({ theme }) => theme.token.next.boxShadowFocus};
  `,
  errorInteraction: css`
    border-color: ${({ theme }) => theme.token.next.colorError};
    box-shadow: ${({ theme }) => theme.token.next.boxShadowError};
  `,
  disabled: css`
    border-color: ${({ theme }) => theme.token.next.colorBgDisabled};
    box-shadow: none;
  `,
  focusRing: css`
    box-shadow:
      0 0 0 2px ${({ theme }) => theme.token.colorBgContainer},
      0 0 0 3px ${({ theme }) => theme.token.next.colorBorderFocus};
  `,
};

/* The map to antd Modal sizes */
const modalWidthConstraints = css`
  .sm_modal {
    width: 400px !important;
  }

  .md_modal {
    width: 600px !important;
  }

  .lg_modal {
    width: 800px !important;
  }

  .xl_modal {
    width: 968px !important;
  }

  .xxl_modal {
    width: 1280px !important;
  }
`;

const modalStyles = css`
  .ant-modal {
    .ant-modal-content {
      padding: ${({ theme }) => theme.token.paddingXL}px;
    }
  }

  /* styling for modal rendered by Modal component */
  .ant-modal-next {
    .ant-modal-content {
      padding: 0;

      .ant-modal-header {
        padding: ${({ theme }) => theme.token.paddingLG}px;
        padding-bottom: 0;

        + .ant-modal-body {
          padding-top: 0;
        }
      }
      .ant-modal-body {
        padding: ${({ theme }) => theme.token.paddingLG}px;
      }
      .ant-modal-footer {
        padding: ${({ theme }) => theme.token.padding}px;
        margin-top: 0;
      }
      .ant-modal-close {
        top: 16px;
        right: 16px;
      }
    }
  }

  /* styling for modal rendered by useModal */
  .ant-modal-next-content-wrapper {
    .ant-modal-content {
      padding: 0;

      .ant-modal-confirm-body {
        padding: ${({ theme }) => theme.token.paddingLG}px;
      }
      .ant-model-next-confirm-footer {
        padding: ${({ theme }) => theme.token.padding}px;
      }
      .ant-modal-close {
        top: 16px;
        right: 16px;
      }
    }
  }
`;

const notificationDefaultStyle = css`
  .success-notification {
    border: 1px solid ${({ theme }) => theme.token.colorSuccessBorder};
  }

  .warning-notification {
    border: 1px solid ${({ theme }) => theme.token.colorWarningBorder};
  }

  .error-notification {
    border: 1px solid ${({ theme }) => theme.token.colorErrorBorder};
  }

  .info-notification {
    border: 1px solid ${({ theme }) => theme.token.colorInfoBorder};
  }

  .next-notification {
    padding: 0.75rem !important;
    border-radius: 8px !important;

    .ant-notification-notice-message {
      margin-bottom: 0 !important;
      margin-inline-start: 24px !important;
    }

    .ant-notification-notice-icon {
      height: 24px !important;
      display: flex;
      align-items: center;
    }

    .ant-notification-notice-close {
      width: 14px !important;
      top: 12px !important;
      right: 12px !important;
    }
  }

  .next-success-notification {
    background: ${({ theme }) => theme.token.next.palette.lime50} !important;
    border: 1px solid ${({ theme }) => theme.token.next.palette.lime100} !important;

    .ant-notification-notice-icon,
    .ant-notification-notice-message,
    .ant-notification-notice-close {
      color: ${({ theme }) => theme.token.next.palette.lime700} !important;
    }
  }

  .next-error-notification {
    background: ${({ theme }) => theme.token.next.palette.red50} !important;
    border: 1px solid ${({ theme }) => theme.token.next.palette.red100} !important;

    .ant-notification-notice-icon,
    .ant-notification-notice-message,
    .ant-notification-notice-close {
      color: ${({ theme }) => theme.token.next.palette.red500} !important;
    }
  }

  .next-warning-notification {
    background: ${({ theme }) => theme.token.next.palette.amber50} !important;
    border: 1px solid ${({ theme }) => theme.token.next.palette.amber100} !important;

    .ant-notification-notice-icon,
    .ant-notification-notice-message,
    .ant-notification-notice-close {
      color: ${({ theme }) => theme.token.next.palette.amber500} !important;
    }
  }

  .next-info-notification {
    background: ${({ theme }) => theme.token.next.palette.grey50} !important;
    border: 1px solid ${({ theme }) => theme.token.next.palette.grey100} !important;

    .ant-notification-notice-icon,
    .ant-notification-notice-message,
    .ant-notification-notice-close {
      color: ${({ theme }) => theme.token.next.palette.grey800} !important;
    }
  }
`;

const messageDefaultStyle = css`
  .loading-message .ant-message-notice-content,
  .info-message .ant-message-notice-content {
    box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
    border: 1px solid ${({ theme }) => theme.token.colorPrimaryBase};
    border-radius: ${({ theme }) => theme.token.borderRadius}px;
    background-color: ${({ theme }) => theme.token.colorPrimaryBg};

    svg {
      color: ${({ theme }) => theme.token.colorPrimaryBase};
      margin-right: ${({ theme }) => theme.token.marginXXS}px;
    }
  }

  .success-message .ant-message-notice-content {
    box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
    border: 1px solid ${({ theme }) => theme.token.colorSuccessBorder};
    border-radius: ${({ theme }) => theme.token.borderRadius}px;
    background-color: ${({ theme }) => theme.token.colorSuccessBg};

    svg {
      color: ${({ theme }) => theme.token.colorSuccessBorder};
      margin-right: ${({ theme }) => theme.token.marginXXS}px;
    }
  }

  .error-message .ant-message-notice-content {
    box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
    border: 1px solid ${({ theme }) => theme.token.colorErrorBorder};
    border-radius: ${({ theme }) => theme.token.borderRadius}px;
    background-color: ${({ theme }) => theme.token.colorErrorBg};

    svg {
      color: ${({ theme }) => theme.token.colorErrorBorder};
      margin-right: ${({ theme }) => theme.token.marginXXS}px;
    }
  }

  .warning-message .ant-message-notice-content {
    box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
    border: 1px solid ${({ theme }) => theme.token.colorWarningBase};
    border-radius: ${({ theme }) => theme.token.borderRadius}px;
    background-color: ${({ theme }) => theme.token.colorWarningBg};

    svg {
      color: ${({ theme }) => theme.token.colorWarningBase};
      margin-right: ${({ theme }) => theme.token.marginXXS}px;
    }
  }
`;

const misc = css`
  .ant-layout {
    background-color: ${({ theme }) => theme.token.colorBgContainer};
  }

  /* TODO: export antd tweaks from the ui-library */
  .ant-btn > .ant-btn-loading-icon .anticon {
    padding-right: 0px;
  }

  .ant-picker-now {
    display: none !important;
  }

  /* TODO: replace these with the actual Alert */
  .alert {
    position: relative;
    padding: 0.75rem 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
  }

  .alert-link {
    font-weight: 100;
  }

  /* These callouts are half-migrated: the backgrounds already follow the
     theme, but the ink and borders were left as fixed light-theme values. In
     dark mode the surface goes dark while the text stays dark blue — 1.71:1
     against colorPrimaryBg. Pair each with the token that belongs to its
     background so both themes stay legible. */
  .alert-primary {
    color: ${({ theme }) => theme.token.colorPrimaryText};
    background-color: ${({ theme }) => theme.token.colorPrimaryBg};
    border-color: ${({ theme }) => theme.token.colorPrimaryBorder};
  }

  .alert-secondary {
    color: ${({ theme }) => theme.token.colorTextSecondary};
    background-color: ${({ theme }) => theme.token.colorFillSecondary};
    border-color: ${({ theme }) => theme.token.colorBorderSecondary};
  }

  .alert-secondary .alert-link {
    color: ${({ theme }) => theme.token.colorLink};
  }
`;

const datePickerStyle = `
  .ant-picker-dropdown .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border: unset;
  }
`;

const dropdownMenuStyle = css`
  .ant-dropdown-menu {
    max-height: 50vh;
    overflow-y: auto;
    border-radius: 8px !important;
  }

  .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
    color: ${({ theme }) => theme.token.colorError} !important;
    background-color: ${({ theme }) => theme.token.colorErrorBgHover} !important;
  }

  .ant-dropdown-menu-title-content {
    display: flex;
    align-items: center;
  }

  .ant-dropdown-next {
    .ant-dropdown-menu {
      border-radius: 12px !important;
      border: 1px solid ${({ theme }) => theme.token.next.colorBorder};
      padding: 6px;

      .ant-dropdown-menu-item:not(
          .ant-dropdown-menu-item-disabled,
          .ant-dropdown-menu-item-disabled:hover
        ):hover {
        background-color: ${({ theme }) => theme.token.next.palette.grey50} !important;

        &.ant-dropdown-menu-item-danger {
          background-color: ${({ theme }) => theme.token.next.palette.red50} !important;
          color: ${({ theme }) => theme.token.next.palette.red500} !important;
        }
      }

      .ant-dropdown-menu-item-divider {
        background-color: ${({ theme }) => theme.token.next.palette.grey75};
      }
    }
  }
`;

const tableSearchStyle = `
  .ant-dropdown .ant-table-filter-dropdown {
    border-radius: 8px;
  }
`;

const commandBarStyle = `
  #commandbar-toast-container {
    z-index: 499 !important;
  }
`;

const variableDeclaration = css`
  :root {
    --sidebar-width: 258px;
    --sidebar-collapsed-width: 64px;
  }
`;

export const styles = css`
  ${variableDeclaration}

  ${misc}

  ${utilities}

  /* Modal */
  ${modalWidthConstraints}
  ${modalStyles}

  /* Tooltip */
  ${tooltipDefaultStyle}

  /* Popover */
  ${popoverCommonStyle}
  ${popoverDefaultStyle}
  ${popoverSecondaryStyle}
  ${popoverPromotionalStyle}
  ${popoverNextStyle}

  /* Notification */
  ${notificationDefaultStyle}

  /* Message */
  ${messageDefaultStyle}
  ${datePickerStyle}

  /* DropdownMenu */
  ${dropdownMenuStyle}

  /* Table Search */
  ${tableSearchStyle}

  /* Adjust CommandBar z-index to Ant Design */
  ${commandBarStyle}
`;
